import { Text } from '../../../text/text.component';

type PdfBubbleProps = {
  src?: string;
  filename?: string;
  fileSizeBytes?: number;
  onClickPreview?: () => void;
  previewTrackingId?: string;
};

const DEFAULT_FILENAME = 'document.pdf';

const formatFileSize = (bytes?: number) => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return undefined;
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exp;
  const rounded = value >= 10 || exp === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[exp]}`;
};

export const PdfBubble = ({ src, filename, fileSizeBytes, onClickPreview, previewTrackingId }: PdfBubbleProps) => {
  const displayName = filename || DEFAULT_FILENAME;
  const formattedSize = formatFileSize(fileSizeBytes);

  const handlePreview = () => {
    if (onClickPreview) {
      onClickPreview();
      return;
    }
    if (src) window.open(src, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className='pdf-bubble-wrapper'>
      {src && (
        <div
          className='pdf-bubble-preview'
          role='button'
          tabIndex={0}
          onClick={handlePreview}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handlePreview();
            }
          }}
          aria-label={`Preview ${displayName}`}
        >
          <object
            data={`${src}#page=1&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
            type='application/pdf'
            className='pdf-bubble-preview-object'
            aria-hidden='true'
          />
          <div className='pdf-bubble-preview-overlay' aria-hidden='true' />
        </div>
      )}
      <div
        className='pdf-bubble'
        role='button'
        tabIndex={0}
        onClick={handlePreview}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePreview();
          }
        }}
        {...(previewTrackingId ? { 'data-trackingid': previewTrackingId } : {})}
      >
        {/* svg with clipped corner to mimic design, <Icon> probably wouldn't have worked as well as this  */}
        <div className='pdf-bubble-icon' aria-hidden='true'>
          <svg className='pdf-bubble-icon-shape' viewBox='0 0 32 40' width='32' height='40' preserveAspectRatio='none'>
            <path d='M5 0 H22 L32 10 V35 Q32 40 27 40 H5 Q0 40 0 35 V5 Q0 0 5 0 Z' fill='currentColor' />
          </svg>
          <span className='pdf-bubble-badge'>PDF</span>
        </div>
        <div className='pdf-bubble-info'>
          <Text size='medium' weight='bold' className='pdf-bubble-filename' color='primary'>
            {displayName}
          </Text>
          {formattedSize && (
            <Text size='small' className='pdf-bubble-filesize' color='subdued'>
              {formattedSize}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
